import React, { useState } from 'react';
import { 
  X, 
  TrendingUp, 
  TrendingDown, 
  Eye, 
  Users, 
  Target,
  Lightbulb,
  FileText,
  Play,
  BarChart3,
  DollarSign,
  Clock,
  Star,
  Award,
  Calendar,
  Zap,
  CheckCircle,
  XCircle
} from 'lucide-react';
import './TopBriefModal.css';

const TopBriefModal = ({ brief, onClose, onViewBrief }) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Mock data specific to this brief (replace with real data)
  const briefStats = {
    title: brief.title,
    totalViews: brief.views,
    totalEngagement: Math.round(brief.views * 0.045), // 4.5% engagement rate
    avgCTR: brief.ctr,
    avgConversion: brief.conversion,
    totalRevenue: Math.round(brief.views * brief.conversion * 0.01 * 15), // Estimated revenue
    scriptsCount: brief.contentCount,
    successRate: brief.successRate,
    avgRating: 4.6,
    completionRate: 78,
    trend: '+18%',
    createdDate: '2024-01-15',
    deadline: '2024-02-15',
    status: 'active',
    topPerformingScripts: [
      {
        title: "Problem-Solution Hook",
        views: 420000,
        ctr: 6.8,
        conversion: 3.9,
        usageCount: 8,
        avgRating: 4.6
      },
      {
        title: "Story Hook",
        views: 380000,
        ctr: 6.2,
        conversion: 3.5,
        usageCount: 6,
        avgRating: 4.4
      },
      {
        title: "Question Hook",
        views: 320000,
        ctr: 5.8,
        conversion: 3.2,
        usageCount: 5,
        avgRating: 4.3
      }
    ],
    worstPerformingScripts: [
      {
        title: "Generic Introduction",
        views: 85000,
        ctr: 3.1,
        conversion: 1.6,
        usageCount: 3,
        avgRating: 3.1
      },
      {
        title: "Product List",
        views: 72000,
        ctr: 2.8,
        conversion: 1.4,
        usageCount: 2,
        avgRating: 2.9
      }
    ]
  };

  const briefInsights = [
    {
      type: "positive",
      title: "High Script Success Rate",
      insight: `${briefStats.successRate}% of scripts created for this brief are performing above average`,
      recommendation: "Continue using this brief structure for similar campaigns"
    },
    {
      type: "improvement",
      title: "Script Quality Variance",
      insight: "Performance varies significantly between scripts (${briefStats.topPerformingScripts[0].views.toLocaleString()} vs ${briefStats.worstPerformingScripts[0].views.toLocaleString()} views)",
      recommendation: "Provide more detailed script guidelines to creators"
    },
    {
      type: "warning",
      title: "Engagement Opportunity",
      insight: "Average watch time is 45 seconds, but brief asks for 60-second content",
      recommendation: "Consider shorter content requirements or better hook strategies"
    }
  ];

  return (
    <div className="top-brief-modal-overlay" onClick={onClose}>
      <div className="top-brief-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="top-brief-modal-header">
          <div className="top-brief-header-content">
            <div className="top-brief-title-section">
              <h2>{briefStats.title}</h2>
              <div className="top-brief-badge">Top Performing Brief</div>
            </div>
            <button className="top-brief-view-btn" onClick={() => onViewBrief(brief)}>
              <FileText size={16} />
              View Brief
            </button>
          </div>
          <button className="top-brief-modal-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="top-brief-modal-tabs">
          <button 
            className={`top-brief-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <BarChart3 size={16} />
            Brief Overview
          </button>
          <button 
            className={`top-brief-tab-btn ${activeTab === 'scripts' ? 'active' : ''}`}
            onClick={() => setActiveTab('scripts')}
          >
            <FileText size={16} />
            Scripts Performance
          </button>
          <button 
            className={`top-brief-tab-btn ${activeTab === 'insights' ? 'active' : ''}`}
            onClick={() => setActiveTab('insights')}
          >
            <Lightbulb size={16} />
            Brief Insights
          </button>
        </div>

        <div className="top-brief-modal-content">
          {activeTab === 'overview' && (
            <div className="top-brief-overview-tab">
              <div className="top-brief-metrics-section">
                <h3>Brief Performance Metrics</h3>
                <div className="top-brief-metrics-grid">
                  <div className="top-brief-metric-card">
                    <div className="top-brief-metric-icon">
                      <Eye size={18} />
                    </div>
                    <div className="top-brief-metric-content">
                      <span className="top-brief-metric-value">{(briefStats.totalViews / 1000).toFixed(0)}K</span>
                      <span className="top-brief-metric-label">Total Views</span>
                    </div>
                  </div>
                  <div className="top-brief-metric-card">
                    <div className="top-brief-metric-icon">
                      <Users size={18} />
                    </div>
                    <div className="top-brief-metric-content">
                      <span className="top-brief-metric-value">{briefStats.totalEngagement.toLocaleString()}</span>
                      <span className="top-brief-metric-label">Engagement</span>
                    </div>
                  </div>
                  <div className="top-brief-metric-card">
                    <div className="top-brief-metric-icon">
                      <Target size={18} />
                    </div>
                    <div className="top-brief-metric-content">
                      <span className="top-brief-metric-value">{briefStats.avgCTR}%</span>
                      <span className="top-brief-metric-label">Avg CTR</span>
                    </div>
                  </div>
                  <div className="top-brief-metric-card">
                    <div className="top-brief-metric-icon">
                      <TrendingUp size={18} />
                    </div>
                    <div className="top-brief-metric-content">
                      <span className="top-brief-metric-value">{briefStats.avgConversion}%</span>
                      <span className="top-brief-metric-label">Conversion</span>
                    </div>
                  </div>
                  <div className="top-brief-metric-card">
                    <div className="top-brief-metric-icon">
                      <DollarSign size={18} />
                    </div>
                    <div className="top-brief-metric-content">
                      <span className="top-brief-metric-value">${(briefStats.totalRevenue / 1000).toFixed(1)}K</span>
                      <span className="top-brief-metric-label">Revenue</span>
                    </div>
                  </div>
                  <div className="top-brief-metric-card">
                    <div className="top-brief-metric-icon">
                      <Award size={18} />
                    </div>
                    <div className="top-brief-metric-content">
                      <span className="top-brief-metric-value">{briefStats.successRate}%</span>
                      <span className="top-brief-metric-label">Success Rate</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="top-brief-summary-section">
                <h3>Brief Summary</h3>
                <div className="top-brief-summary-card">
                  <p><strong>{briefStats.title}</strong> has generated <strong>{(briefStats.totalViews / 1000000).toFixed(1)}M views</strong> across <strong>{briefStats.scriptsCount} scripts</strong> with a <strong>{briefStats.successRate}% success rate</strong>.</p>
                  <p>The brief has an average CTR of <strong>{briefStats.avgCTR}%</strong> and conversion rate of <strong>{briefStats.avgConversion}%</strong>, generating <strong>${briefStats.totalRevenue.toLocaleString()} in revenue</strong>.</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'scripts' && (
            <div className="top-brief-scripts-tab">
              <div className="top-brief-scripts-section">
                <h3>Top Performing Scripts</h3>
                <div className="top-brief-scripts-grid">
                  {briefStats.topPerformingScripts.map((script, index) => (
                                         <div key={index} className="top-brief-script-card">
                       <div className="top-brief-script-header">
                         <h4>{script.title}</h4>
                       </div>
                      <div className="top-brief-script-metrics">
                        <div className="top-brief-script-metric">
                          <span className="top-brief-script-value">{(script.views / 1000).toFixed(0)}K</span>
                          <span className="top-brief-script-label">Views</span>
                        </div>
                        <div className="top-brief-script-metric">
                          <span className="top-brief-script-value">{script.ctr}%</span>
                          <span className="top-brief-script-label">CTR</span>
                        </div>
                        <div className="top-brief-script-metric">
                          <span className="top-brief-script-value">{script.conversion}%</span>
                          <span className="top-brief-script-label">Conversion</span>
                        </div>
                        <div className="top-brief-script-metric">
                          <span className="top-brief-script-value">{script.usageCount}</span>
                          <span className="top-brief-script-label">Usage</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="top-brief-worst-scripts-section">
                <h3>Scripts Needing Improvement</h3>
                <div className="top-brief-scripts-grid">
                                     {briefStats.worstPerformingScripts.map((script, index) => (
                     <div key={index} className="top-brief-script-card worst">
                       <div className="top-brief-script-header">
                         <h4>{script.title}</h4>
                       </div>
                      <div className="top-brief-script-metrics">
                        <div className="top-brief-script-metric">
                          <span className="top-brief-script-value">{(script.views / 1000).toFixed(0)}K</span>
                          <span className="top-brief-script-label">Views</span>
                        </div>
                        <div className="top-brief-script-metric">
                          <span className="top-brief-script-value">{script.ctr}%</span>
                          <span className="top-brief-script-label">CTR</span>
                        </div>
                        <div className="top-brief-script-metric">
                          <span className="top-brief-script-value">{script.conversion}%</span>
                          <span className="top-brief-script-label">Conversion</span>
                        </div>
                        <div className="top-brief-script-metric">
                          <span className="top-brief-script-value">{script.usageCount}</span>
                          <span className="top-brief-script-label">Usage</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'insights' && (
            <div className="top-brief-insights-tab">
              <div className="top-brief-insights-section">
                <h3>Brief Performance Insights</h3>
                <div className="top-brief-insights-grid">
                  {briefInsights.map((insight, index) => (
                    <div key={index} className={`top-brief-insight-card ${insight.type}`}>
                      <div className="top-brief-insight-header">
                        <div className="top-brief-insight-icon">
                          {insight.type === 'positive' && <CheckCircle size={16} />}
                          {insight.type === 'improvement' && <Target size={16} />}
                          {insight.type === 'warning' && <XCircle size={16} />}
                        </div>
                        <h4>{insight.title}</h4>
                      </div>
                      <p className="top-brief-insight-text">{insight.insight}</p>
                      <div className="top-brief-insight-recommendation">
                        <strong>Recommendation:</strong> {insight.recommendation}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopBriefModal;
